/**
 * Editing a client that already exists.
 *
 * Until this form there was no way to change a client at all: the only mutation
 * in the whole app was the status pill. That was survivable while every client
 * was typed in here by hand, and stopped being survivable when 39 of them
 * arrived from the old CRM - 24 with no registry code and all 39 with no
 * e-mail, because Perfex kept the address on the contact rather than the
 * company. Data you cannot correct is data you stop trusting.
 *
 * Two rules make it safe to point at the business register from an edit form:
 *
 *  - the register may fill a blank and may never overwrite a typed value
 *    (`prefillFromRegister`), and the form says afterwards what it filled and
 *    what it deliberately left alone;
 *  - only the fields that actually changed are sent (`changedFields`), so
 *    saving does not quietly rewrite a column this form only ever read.
 */
import { useEffect, useState } from "react";
import { AddressAutocomplete, LocaleCombobox } from "@wa/ui";
import { Loader2 } from "lucide-react";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { duplicateRegCode, updateClient, type Client, type ClientKind } from "../../../lib/clients";
import type { StaffMember } from "../../../lib/support";
import { staffOptionLabel } from "../../../lib/staff";
import { changedFields, prefillFromRegister, type PrefillableForm } from "../../../lib/clientPrefill";
import { Btn, Modal, SelectInput, useToast } from "../../ui";
import { FALLBACK_DOC_LOCALES } from "../../../lib/docLocales";
import { useAddressSource } from "../../../lib/address";
import { getNumbering } from "../../../lib/sales";
import { AriregisterNameInput, type ArPrefill } from "./AriregisterNameInput";
import { Field, Pill } from "./formBits";

/** Every text field this form owns. Kept in one place so the patch and the
 *  initial state cannot drift apart. */
const TEXT_FIELDS = [
  "name",
  "regCode",
  "vatNumber",
  "email",
  "phone",
  "website",
  "addressLine",
  "city",
  "zip",
  "country",
  "currency",
  "notes",
] as const;

type TextField = (typeof TEXT_FIELDS)[number];
type TextState = Record<TextField, string>;

function initialText(c: Client): TextState {
  const out = {} as TextState;
  for (const key of TEXT_FIELDS) {
    const v = (c as unknown as Record<string, unknown>)[key];
    out[key] = v == null ? "" : String(v);
  }
  return out;
}

export function ClientEditModal({
  client,
  staff,
  onClose,
  onSaved,
}: {
  client: Client;
  /** Already in directory order - active people first. See `_shared/staff.ts`. */
  staff: StaffMember[];
  onClose: () => void;
  onSaved: (c: Client) => void;
}) {
  const t = useT();
  const { locale: uiLocale } = useI18n();
  const addressSource = useAddressSource();
  const toast = useToast();

  const [text, setText] = useState<TextState>(() => initialText(client));
  const [kind, setKind] = useState<ClientKind>(client.kind);
  const [assignedTo, setAssignedTo] = useState(client.assignedTo ?? "");
  const [terms, setTerms] = useState(client.paymentTermsDays == null ? "" : String(client.paymentTermsDays));
  // "" is the empty option, which stores null - "nobody chose, follow the desk".
  const [locale, setLocale] = useState(client.locale ?? "");
  // The languages the document PDF can print. Asked for once when the form
  // opens; the picker falls back to the two in the bundle until it answers, so
  // a slow or older API leaves a usable field rather than an empty one.
  const [docLocales, setDocLocales] = useState<string[]>(FALLBACK_DOC_LOCALES);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void getNumbering()
      .then((cfg) => { if (live && cfg.docLocales?.length) setDocLocales(cfg.docLocales); })
      .catch(() => { /* the fallback list is already in state */ });
    return () => { live = false; };
  }, []);

  /** What the last register pick did, so the operator is told rather than surprised. */
  const [prefillNote, setPrefillNote] = useState<{ filled: string[]; kept: string[] } | null>(null);

  function set(key: TextField, value: string): void {
    setText((prev) => ({ ...prev, [key]: value }));
  }

  /**
   * A register pick fills the blanks and reports the rest.
   *
   * The name is the exception: the operator chose this company from the list,
   * so replacing the name is the thing they asked for.
   */
  function applyPrefill(p: ArPrefill): void {
    setText((prev) => {
      const form: PrefillableForm = {
        regCode: prev.regCode,
        vatNumber: prev.vatNumber,
        email: prev.email,
        phone: prev.phone,
        website: prev.website,
        addressLine: prev.addressLine,
      };
      const { next, filled, kept } = prefillFromRegister(form, p);
      setPrefillNote({ filled, kept });
      return { ...prev, ...next, name: p.name };
    });
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!text.name.trim() || busy) return;
    setBusy(true);
    try {
      const patch: Record<string, unknown> = changedFields(
        client as unknown as Record<string, unknown>,
        text,
        TEXT_FIELDS,
      );
      if (kind !== client.kind) patch.kind = kind;
      if ((assignedTo || null) !== client.assignedTo) patch.assignedTo = assignedTo || null;

      const termsNum = terms.trim() === "" ? null : Number(terms);
      if (termsNum !== client.paymentTermsDays && !(termsNum != null && Number.isNaN(termsNum))) {
        patch.paymentTermsDays = termsNum;
      }

      // `|| null` on both sides: the field holds "" for "not chosen" and the
      // column holds null, and comparing the two raw would send a patch on
      // every save of a client who has never had a language.
      if ((locale || null) !== (client.locale ?? null)) patch.locale = locale || null;

      // Nothing to send is a normal outcome, not an error: the operator opened
      // the form, looked, and closed it.
      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }

      const saved = await updateClient(client.id, patch);
      onSaved(saved);
      onClose();
    } catch (err) {
      const dup = duplicateRegCode(err);
      toast(dup?.clientId ? t("clients.duplicateRegCode", { name: dup.name }) : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const fieldLabel = (f: string): string => t(`clients.${f === "addressLine" ? "address" : f}`);

  return (
    <Modal title={t("clients.editTitle")} label={t("clients.editTitle")} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={t("clients.name")}>
          <AriregisterNameInput
            pill
            value={text.name}
            onChange={(v) => set("name", v)}
            onPick={applyPrefill}
            placeholder={t("clients.namePlaceholder")}
            required
            enabled={kind === "company"}
            hint={kind === "company" ? t("clients.editRegisterHint") : undefined}
          />
        </Field>

        {prefillNote ? (
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            {prefillNote.filled.length > 0
              ? t("clients.prefillFilled", { fields: prefillNote.filled.map(fieldLabel).join(", ") })
              : t("clients.prefillNothing")}
            {prefillNote.kept.length > 0
              ? ` ${t("clients.prefillKept", { fields: prefillNote.kept.map(fieldLabel).join(", ") })}`
              : ""}
          </p>
        ) : null}

        <Field label={t("clients.type")}>
          <SelectInput value={kind} onChange={(next) => setKind(next as ClientKind)} label={t("clients.type")}>
            <option value="company">{t("clients.company")}</option>
            <option value="person">{t("clients.person")}</option>
          </SelectInput>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Field label={t("clients.regCode")}>
              <Pill value={text.regCode} onChange={(v) => set("regCode", v)} placeholder="12345678" inputMode="numeric" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.vat")}>
              <Pill value={text.vatNumber} onChange={(v) => set("vatNumber", v)} placeholder="EE123456789" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.email")}>
              <Pill value={text.email} onChange={(v) => set("email", v)} placeholder={t("common.example.email")} type="email" inputMode="email" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.phone")}>
              <Pill value={text.phone} onChange={(v) => set("phone", v)} placeholder="+372 5xxx xxxx" inputMode="tel" />
            </Field>
          </div>
        </div>

        <Field label={t("clients.website")}>
          <Pill value={text.website} onChange={(v) => set("website", v)} placeholder={t("common.example.website")} inputMode="url" />
        </Field>

        <Field label={t("clients.address")}>
          {/* A picked address fills the street line, the city, the postcode and the country. */}
          <AddressAutocomplete
            className="crm-inp"
            inputClassName="h-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
            source={addressSource}
            fill="street"
            value={text.addressLine}
            onChange={(v) => set("addressLine", v)}
            onPick={(place) => {
              set("addressLine", place.street || place.formatted);
              if (place.city) set("city", place.city);
              if (place.postalCode) set("zip", place.postalCode);
              if (place.country) set("country", place.country);
            }}
            placeholder={t("clients.addressPlaceholder")}
            aria-label={t("clients.address")}
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Field label={t("clients.city")}>
              <Pill value={text.city} onChange={(v) => set("city", v)} placeholder="Tallinn" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.zip")}>
              <Pill value={text.zip} onChange={(v) => set("zip", v)} placeholder="10123" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.country")}>
              <Pill value={text.country} onChange={(v) => set("country", v)} placeholder="EE" />
            </Field>
          </div>
        </div>

        <Field label={t("clients.assignedTo")}>
          <SelectInput value={assignedTo} onChange={setAssignedTo} label={t("clients.assignedTo")}>
            <option value="">{t("clients.unassigned")}</option>
            {/* A leaver stays in the list and stays selectable: this client may
                already be theirs, and an owner with no option to match would
                blank the field on the next save. The mark goes in the label
                rather than beside it, so the row reads the same wherever this
                option list is drawn. */}
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {staffOptionLabel(s, t("common.inactive"))}
              </option>
            ))}
          </SelectInput>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Field label={t("clients.paymentTerms")}>
              <Pill value={terms} onChange={setTerms} placeholder="14" inputMode="numeric" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.currency")}>
              <Pill value={text.currency} onChange={(v) => set("currency", v)} placeholder="EUR" />
            </Field>
          </div>
        </div>

        {/* Beside the payment term and the currency, because it is the same
            sort of standing answer: what this client's documents say before
            anybody types into one. */}
        <Field label={t("clients.docLocale")} hint={t("clients.docLocaleHint")}>
          {/* Empty is a real answer here: the site's default language. The clear
              control takes the client back to it. */}
          <LocaleCombobox
            className="h-[52px] border-0 px-[18px] text-[13.5px] shadow-none"
            popoverClassName="rounded-2xl"
            locales={docLocales}
            displayLocale={uiLocale}
            value={locale}
            onChange={setLocale}
            clearable
            clearLabel={t("clients.docLocaleDefault")}
            placeholder={t("clients.docLocaleDefault")}
            aria-label={t("clients.docLocale")}
            searchPlaceholder={t("common.localeSearch")}
            emptyLabel={t("common.localeNone")}
          />
        </Field>

        <Field label={t("clients.notes")}>
          <textarea
            className="crm-area"
            value={text.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder={t("clients.notesPlaceholder")}
          />
        </Field>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("clients.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !text.name.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("clients.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
